import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useWorkspace } from '../../../app/WorkspaceContext';
import { useTranslation } from '../../../app/LanguageContext';
import { deleteWorkspace } from '../../../app/api';
import ConfirmDialog from './ConfirmDialog';

const WorkspaceSelector = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation();
  const { workspaces, currentWorkspace, setCurrentWorkspace, refreshWorkspaces, loading } = useWorkspace();
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (ws) => {
    setCurrentWorkspace(ws);
    handleClose();
    navigate('/');
    window.location.reload();
  };

  const handleDeleteClick = (e, ws) => {
    e.stopPropagation();
    setDeleteConfirm(ws);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteWorkspace(deleteConfirm.id);
      await refreshWorkspaces();
      if (currentWorkspace?.id === deleteConfirm.id) {
        const remaining = workspaces.filter((w) => w.id !== deleteConfirm.id);
        const next = remaining[0];
        if (next) {
          setCurrentWorkspace(next);
          navigate('/');
          window.location.reload();
        }
      }
    } catch (_) {}
    setDeleteConfirm(null);
    handleClose();
  };

  if (loading || workspaces.length === 0) {
    return null;
  }

  return (
    <>
      {isNarrow ? (
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label={translate('workspace.select_workspace')}
          sx={{ mr: 0.5 }}
          size="small"
        >
          <BusinessIcon />
        </IconButton>
      ) : (
        <Button
          color="inherit"
          startIcon={<BusinessIcon />}
          endIcon={<KeyboardArrowDownIcon sx={{ opacity: 0.92 }} />}
          onClick={handleOpen}
          sx={{
            mr: { xs: 1, sm: 2 },
            textTransform: 'none',
            maxWidth: 220,
            minWidth: 0,
            color: 'inherit',
            '& .MuiTypography-root': { color: 'inherit' },
          }}
        >
          <Typography variant="body2" noWrap sx={{ maxWidth: 140, color: 'inherit', fontWeight: 500 }}>
            {currentWorkspace?.name || 'Workspace'}
          </Typography>
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {workspaces.map((ws) => (
          <MenuItem key={ws.id} onClick={() => handleSelect(ws)}>
            <ListItemIcon>
              {currentWorkspace?.id === ws.id ? (
                <CheckIcon fontSize="small" />
              ) : (
                <BusinessIcon fontSize="small" sx={{ opacity: 0.5 }} />
              )}
            </ListItemIcon>
            <ListItemText primary={ws.name} secondary={ws.is_personal ? translate('workspace.personal') : ''} />
            {!ws.is_personal && (
              <IconButton size="small" onClick={(e) => handleDeleteClick(e, ws)} color="error" sx={{ ml: 1 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { handleClose(); navigate('/workspaces/new'); }}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translate('workspace.create_workspace')} />
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={Boolean(deleteConfirm)}
        title={translate('workspace.delete_confirm_title')}
        message={deleteConfirm ? `${translate('workspace.delete_confirm_message')} ${deleteConfirm.name}?` : ''}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        confirmText={translate('common.delete')}
      />
    </>
  );
};

export default WorkspaceSelector;
